import { Injectable } from "@angular/core";
import {
  deleteToken,
  getMessaging,
  getToken,
  Messaging,
  onMessage,
  isSupported,
} from "firebase/messaging";
import { initializeApp, getApps } from "firebase/app";
import { BehaviorSubject } from "rxjs";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class MessagingService {
  private _$currentMessage: BehaviorSubject<any> = new BehaviorSubject(null);
  messaging: Messaging | any;
  private swRegistration: ServiceWorkerRegistration | null = null;
  private foregroundListenerAttached = false;
  private initPromise: Promise<void> | null = null;

  constructor() {
    // Intentionally empty — FCM init must not compete with white-label theme API.
  }

  /** Start Firebase messaging after theme bootstrap has begun. */
  ensureInitialized(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.initMessaging();
    }
    return this.initPromise;
  }

  get currentMessage() {
    return this._$currentMessage.asObservable();
  }

  /** True when running as an installed Home Screen app (required for iOS push). */
  isStandalonePwa(): boolean {
    const nav = window.navigator as Navigator & { standalone?: boolean };
    return (
      nav.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches
    );
  }

  isIosDevice(): boolean {
    const ua = window.navigator.userAgent || "";
    const iOS =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    return iOS;
  }

  private async initMessaging() {
    try {
      if (!("Notification" in window) || !("serviceWorker" in navigator)) {
        console.warn("Push notifications are not supported in this browser.");
        return;
      }

      // iOS only supports Web Push inside a Home Screen PWA (iOS 16.4+).
      if (this.isIosDevice() && !this.isStandalonePwa()) {
        console.warn(
          "iOS: open the app from the Home Screen icon to enable notification permission and FCM token."
        );
        return;
      }

      const supported = await isSupported();
      if (!supported) {
        console.warn(
          "Firebase messaging is not supported. On iOS use Home Screen PWA on iOS 16.4+."
        );
        return;
      }

      const app = getApps().length
        ? getApps()[0]
        : initializeApp(environment.firebaseConfig);

      this.swRegistration = await this.registerMessagingServiceWorker();
      if (!this.swRegistration) {
        console.error("Cannot get FCM token without a registered service worker.");
        return;
      }

      this.messaging = getMessaging(app);
      console.log("Firebase messaging initialized. standalone=", this.isStandalonePwa());

      const cachedLogo =
        localStorage.getItem("downloadLogo") || environment.logoImage;
      if (cachedLogo) {
        this.setBrandLogo(cachedLogo);
      }

      // Token fetch only if already granted — never call enableNotifications here
      // (that would dead-lock on ensureInitialized while init is still running).
      // Permission prompt stays on Send OTP user tap.
      if (Notification.permission === "granted") {
        await this.requestToken();
        this.receiveMessage();
      } else if (this.isIosDevice()) {
        console.log(
          "iOS: waiting for a user tap to request notification permission."
        );
      }
    } catch (err) {
      console.error("Failed to initialize Firebase messaging.", err);
    }
  }

  private async registerMessagingServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    try {
      // Root scope works more reliably for iOS Web Push.
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js"
      );
      await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("Service worker ready timeout")), 5000)
        ),
      ]);
      console.log(
        "Firebase messaging service worker registered.",
        registration.scope
      );
      return registration;
    } catch (err) {
      console.error("Firebase messaging service worker registration failed.", err);
      return null;
    }
  }

  async requestToken() {
    // Only wait for init if messaging is not ready yet.
    // Never await ensureInitialized while we are already inside initMessaging.
    if (!this.messaging || !this.swRegistration) {
      await this.ensureInitialized();
    }

    if (!this.messaging) {
      console.warn("Firebase Messaging is not initialized yet.");
      return null;
    }

    if (!this.swRegistration) {
      console.warn("Firebase messaging service worker is not registered yet.");
      return null;
    }

    if (Notification.permission !== "granted") {
      console.warn(
        "Notification permission is not granted. Current permission:",
        Notification.permission
      );
      return null;
    }

    try {
      const token = await getToken(this.messaging, {
        vapidKey: environment.vapidKey,
        serviceWorkerRegistration: this.swRegistration,
      });

      if (token) {
        console.log("firebaseToken :: ", token);
        localStorage.setItem("firebaseToken", token);
        return token;
      }

      console.warn(
        "FCM returned an empty token. Check Firebase project settings and VAPID key."
      );
      return null;
    } catch (err) {
      console.error("Unable to get FCM token.", err);
      return null;
    }
  }

  receiveMessage() {
    void this.ensureInitialized().then(() => {
      if (!this.messaging || this.foregroundListenerAttached) {
        return;
      }

      this.foregroundListenerAttached = true;
      onMessage(this.messaging, (payload: any) => {
        console.log("Message received. ", payload);
        // System notification only (OS top banner + sound) — no in-app toast
        this.showNotification(payload);
        this._$currentMessage.next(payload);
      });
    });
  }

  private async showNotification(payload: any) {
    if (Notification.permission !== "granted") {
      return;
    }

    const title =
      payload?.notification?.title ||
      payload?.data?.title ||
      "Notification";
    // Same tag as SW — prevents stacking if both paths ever fire
    const tag =
      payload?.messageId ||
      payload?.data?.messageId ||
      payload?.collapseKey ||
      payload?.data?.collapseKey ||
      "fcm-push";
    // renotify / vibrate / silent are valid Web Notification fields but missing from TS NotificationOptions
    const options = {
      body:
        payload?.notification?.body ||
        payload?.data?.body ||
        "",
      icon: this.resolveNotificationIcon(payload),
      badge: "/assets/icons/icon-72x72.png",
      data: payload?.data || payload?.notification || {},
      tag,
      renotify: true,
      silent: false,
      vibrate: [180, 80, 180],
    } as NotificationOptions;

    try {
      const registration =
        this.swRegistration || (await navigator.serviceWorker.ready);
      if (registration?.showNotification) {
        await registration.showNotification(title, options);
        return;
      }
    } catch (err) {
      console.warn("Service worker notification failed, falling back.", err);
    }

    try {
      new Notification(title, options);
    } catch (err) {
      console.warn("Notification API failed.", err);
    }
  }

  private resolveNotificationIcon(payload: any): string {
    return (
      payload?.notification?.icon ||
      payload?.data?.icon ||
      payload?.notification?.image ||
      payload?.data?.image ||
      localStorage.getItem("downloadLogo") ||
      environment.logoImage ||
      "/assets/images/header/logo-img.png"
    );
  }

  setBrandLogo(logoUrl: string) {
    if (!logoUrl) {
      return;
    }

    localStorage.setItem("downloadLogo", logoUrl);
    environment.logoImage = logoUrl;

    const postLogo = (worker: ServiceWorker | null | undefined) => {
      worker?.postMessage({ type: "SET_BRAND_LOGO", logoUrl });
    };

    postLogo(this.swRegistration?.active);

    if (this.swRegistration?.installing) {
      this.swRegistration.installing.addEventListener("statechange", () => {
        if (this.swRegistration?.active) {
          postLogo(this.swRegistration.active);
        }
      });
    }

    navigator.serviceWorker?.ready?.then((registration) => {
      postLogo(registration.active);
    });
  }

  async removeToken() {
    try {
      if (this.messaging) {
        await deleteToken(this.messaging);
      }
    } catch (err) {
      console.log("Unable to delete messaging token.", err);
    }
  }

  /**
   * Call this from a user tap (button click). Required for iOS permission prompt.
   * Permission must be requested immediately — any long await before
   * requestPermission() can prevent the iOS dialog from appearing.
   * Never blocks login forever — FCM failures return null quickly.
   */
  async enableNotifications(): Promise<string | null> {
    try {
      if (!("Notification" in window)) {
        console.warn("Notification API not available.");
        return null;
      }

      if (this.isIosDevice() && !this.isStandalonePwa()) {
        console.warn(
          "iOS: notification permission only works when opened from Home Screen."
        );
        return null;
      }

      let permission = Notification.permission;
      console.log("Notification permission:", permission);

      // Ask immediately while still inside the user-gesture call stack (iOS).
      if (permission === "default") {
        permission = await Notification.requestPermission();
        console.log("Notification permission after prompt:", permission);
      }

      await Promise.race([
        this.ensureInitialized(),
        new Promise<void>((resolve) => setTimeout(resolve, 4000)),
      ]);

      if (permission === "granted") {
        const token = await Promise.race([
          this.requestToken(),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000)),
        ]);
        this.receiveMessage();
        return token;
      }

      console.warn(
        "Notification permission denied or blocked. On iPhone: Settings → Notifications → allow for this app."
      );
      return null;
    } catch (err) {
      console.warn("enableNotifications failed; continuing without FCM token.", err);
      return null;
    }
  }

  async requestNotificationAccess(): Promise<string | null> {
    return this.enableNotifications();
  }
}
